/**
 * Action Plan Panel - Main Container Component
 *
 * Displays strategic action plan for a journey with:
 * - Summary header with KPIs (total value, ROI, progress)
 * - 3 view modes: Timeline (Gantt), List (accordion), Matrix (effort-value)
 * - Progress tracking and task status updates
 * - Value realization visualization
 *
 * @see Documentation/Development/Design Docs/Strategic_Action_Plan_Design_And_Implementation_Plan_v2.0.md Section 6
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useActionPlanUrlState } from '../hooks/useActionPlanUrlState';
import { ActionPlanHeader } from './ActionPlanHeader';
// Runtime values (the view vocabulary), not types — a separate import so the
// `import type` block above stays type-only.
import {
  ACTION_PLAN_VIEWS,
  ACTION_PLAN_VIEW_LABELS,
  DEFAULT_ACTION_PLAN_VIEW,
} from '../types/actionPlan';
import { ListView } from './ListView';
import { TimelineView } from './TimelineView';
import { MatrixView } from './MatrixView';
import { ActionPlanTaskDrawer } from './ActionPlanTaskDrawer';
import { ActionPlanModal } from './ActionPlanModal';
import {
  DEFAULT_MATRIX_FILTERS,
  stableTaskOrder,
  type MatrixFilters,
} from './actionPlanMatrixPlacement';
import { ValueRealizationChart } from './ValueRealizationChart';
import { ProgressPanel } from './ProgressPanel';
import { DependencyReviewPanel } from './DependencyReviewPanel';
import { BusinessConfigForm } from './BusinessConfigForm';
import { CostConfigurationPanel } from './CostConfiguration';
import { IntelligencePrism } from './IntelligencePrism';
import { recalculateCosts, extractCostSummary, type Industry } from './CostConfiguration/costModel';
import type {
  ActionPlan,
  TaskStatus,
  GenerateActionPlanRequest,
  GenerateActionPlanResponse,
  UpdateTaskStateResponse
} from '../types/actionPlan';
import type {
  AcknowledgedAssumption,
  AssumptionSummaryEntry,
} from '../types/actionPlanAssumptions';
import type { ProjectCosts, CostSummary } from './CostConfiguration/costTypes';
import { Link } from 'react-router-dom';
import { useJourneyId } from '../contexts/JourneyContext';
import { useApiClient } from '../hooks/useApiClient';
import '../styles/action-plan.css';

/**
 * WHETHER BRIDGE AI RECOMMENDATIONS EXIST — FOUR STATES, NOT A BOOLEAN.
 *
 * This was `boolean | null`, and every failure collapsed into `false`: a 503, a 500, a
 * 401, and a thrown network error all became "this journey does not have any yet". That
 * sentence is a claim about the journey's contents, and three of those four cases are
 * claims about the CONNECTION. The panel told the reader their journey was missing work
 * it had never actually managed to ask about.
 *
 * `null` was worse in the other direction: it fell through to the generate form, so the
 * configuration inputs and the generate button were offered before the one prerequisite
 * the server enforces had been checked at all.
 *
 *   checking     — the request is in flight. Claim nothing; offer nothing.
 *   ready        — the server answered and recommendations exist.
 *   absent       — the server answered and there are none. THE ONLY state permitted to
 *                  say "does not have any yet".
 *   unavailable  — the question could not be answered. Offer a retry, not a verdict.
 */
export type BridgePrerequisite =
  | { state: 'checking' }
  | { state: 'ready'; recommendation_count: number }
  | { state: 'absent' }
  | { state: 'unavailable'; reason: string };

// DC-1639: cross-link to the dataset's domain-level Remediation Action Plan
// (a distinct artifact — the accepted prescriptive triangulation plays).
interface DomainCrossLink {
  exists: boolean;
  domain_id?: string;
  domain_name?: string;
  total: number;
}

/**
 * Read an acknowledged assumption out of the server's summary.
 *
 * The cost model needs an org size and an industry to pick multipliers, and it
 * has no unavailable state. So a MISSING acknowledgment falls back to the cost
 * model's own neutral band — but only for cost-model multipliers, never for a
 * displayed value. The distinction matters: this is a modelling parameter with no
 * user-visible number attached, not a financial figure presented as fact.
 */
function assumptionValue(
  summary: AssumptionSummaryEntry[] | null,
  key: AcknowledgedAssumption['key'],
): string | null {
  const entry = summary?.find((e) => e.key === key);
  if (!entry || entry.provenance === 'unavailable' || entry.value === null) return null;
  return String(entry.value);
}

function sizeFromAssumptions(
  summary: AssumptionSummaryEntry[] | null,
): 'small' | 'medium' | 'large' {
  const raw = assumptionValue(summary, 'organization_size');
  return raw === 'small' || raw === 'large' ? raw : 'medium';
}

function industryFromAssumptions(summary: AssumptionSummaryEntry[] | null): Industry {
  return (assumptionValue(summary, 'industry') as Industry | null) ?? 'pharma';
}

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ActionPlanPanelProps {
  journeyId?: string;
  onClose?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ActionPlanPanel: React.FC<ActionPlanPanelProps> = ({
  journeyId: journeyIdProp,
  onClose: _onClose
}) => {
  const journeyId = useJourneyId(journeyIdProp);
  const { authFetch, post, put } = useApiClient();
  // State: Data
  const [actionPlan, setActionPlan] = useState<ActionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * Recoverable, informational notice shown ALONGSIDE the plan.
   *
   * Distinct from `error`, which renders a full-page "Error Loading Action Plan"
   * and replaces the whole view. Routing a stale-token conflict through `error`
   * hid the very task list the reload had just fetched — the user was told
   * something changed and simultaneously denied the chance to look at it.
   */
  const [notice, setNotice] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [bridgePrerequisite, setBridgePrerequisite] = useState<BridgePrerequisite>({
    state: 'checking',
  });
  // DC-1639: does this dataset's domain have a Remediation Action Plan?
  const [domainCrossLink, setDomainCrossLink] = useState<DomainCrossLink | null>(null);

  // State: UI
  /*
   * `searchParams` is still read directly for ONE thing: `?tab=`, which belongs to
   * JourneyNavigator rather than to this feature (see `goToBridgeIntelligence` below).
   */
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * VIEW AND SELECTION LIVE IN THE URL — implemented in `useActionPlanUrlState`.
   *
   * Extracted from this component so its tests exercise production code: the behaviour was
   * inline here and covered by a hand-written miniature in the test file that re-declared
   * the same logic, and a copy stays green while the original drifts. The rationale for
   * each choice (Matrix default, push vs replace, why the query string is copied and never
   * rebuilt, and why `apTask` is not a candidate `stable_key`) lives with the hook.
   */
  const { viewMode, selectedTaskId, setViewMode, setSelectedTaskId, setViewAndTask } = useActionPlanUrlState();

  /*
   * CANONICAL FILTER STATE (2B part 2).
   *
   * One filter object, owned here rather than inside a view, so it survives switching
   * between Matrix and the supporting views — a per-view copy would be a second source
   * of truth about what the reader has narrowed to. Matrix is the only consumer in this
   * commit; wiring List/Timeline/Prism/Dependency/Value Curve to the same state is open
   * under S4f.
   */
  const [matrixFilters, setMatrixFilters] = useState<MatrixFilters>(DEFAULT_MATRIX_FILTERS);
  const clearMatrixFilters = useCallback(() => setMatrixFilters(DEFAULT_MATRIX_FILTERS), []);

  /*
   * STALE SELECTION CLEARING (2B part 2).
   *
   * `?apTask=` can outlive the task it names — a regenerated plan mints new ids, and a
   * shared URL can name a task from someone else's plan state. A selection that no
   * rendered task carries would open a drawer over nothing.
   *
   * The guard clears ONLY after the active plan has loaded successfully:
   *   - never while `loading` — a valid deep link must survive a slow fetch;
   *   - never against a null plan (404 → no plan exists) or an `error` render — those
   *     say nothing about whether the id is stale;
   *   - via `setSelectedTaskId`, which REPLACES history rather than pushing, and copies
   *     the query string, so `tab`, `apView` and every unrelated parameter survive.
   */
  useEffect(() => {
    if (loading || error || !actionPlan || !selectedTaskId) return;
    if (!actionPlan.tasks.some((t) => t.id === selectedTaskId)) {
      setSelectedTaskId(null);
    }
  }, [loading, error, actionPlan, selectedTaskId, setSelectedTaskId]);

  /*
   * "SHOW ON MATRIX" (S4f-C): one panel-level control serves every supporting
   * view, and it RETURNS FOCUS TO THE SAME TASK — the switch alone would land
   * focus nowhere in particular. The pending id is a ref, consumed by the effect
   * below once the Matrix has rendered the card (located by the same
   * `data-ap-task-button` contract the drawer's focus return uses).
   */
  const focusTaskOnMatrixRef = useRef<string | null>(null);
  const showTaskOnMatrix = useCallback(
    (taskId: string) => {
      focusTaskOnMatrixRef.current = taskId;
      // ONE atomic write: two sequential setSearchParams calls cannot compose
      // within a tick (see the hook), and losing either parameter here means
      // landing on the Matrix with no selection, or selecting without moving.
      setViewAndTask('matrix', taskId);
    },
    [setViewAndTask],
  );
  useEffect(() => {
    if (viewMode !== 'matrix' || !focusTaskOnMatrixRef.current) return;
    const el = document.querySelector<HTMLElement>(
      `[data-ap-task-button="${CSS.escape(focusTaskOnMatrixRef.current)}"]`,
    );
    focusTaskOnMatrixRef.current = null;
    el?.focus();
  }, [viewMode, actionPlan]);

  /*
   * PRISM DRILL-THROUGH (S4f-C): the console-only callbacks are retired. A
   * driver or station click resolves to the highest-value matching task (the
   * same stable order every cell uses) and shows it on the Matrix with the
   * drawer open. When nothing in the PLAN matches, the panel says so as a
   * recoverable notice instead of silently doing nothing — the Prism draws on
   * recommendation data, and not every recommendation became a task.
   */
  const drillToTask = useCallback(
    (predicate: (t: import('../types/actionPlan').Task) => boolean, noneMessage: string) => {
      const candidates = (actionPlan?.tasks ?? []).filter(predicate);
      if (candidates.length === 0) {
        setNotice(noneMessage);
        return;
      }
      const best = [...candidates].sort(stableTaskOrder)[0];
      showTaskOnMatrix(best.id);
    },
    [actionPlan, showTaskOnMatrix],
  );
  const normalizeStation = (value: string): string =>
    value.toLowerCase().replace(/[\s_-]/g, '');

  /**
   * Navigate to Bridge Intelligence through the SUPPORTED route.
   *
   * The previous implementation did
   *     document.querySelector('[data-tab="bridge"]')?.click()
   * with an `alert()` fallback — reaching into a parent component's DOM and, when that
   * failed, telling the user to navigate themselves. `JourneyNavigator` owns `?tab=` and
   * syncs it to its own state, so setting the parameter IS the supported route, and it
   * cannot silently fail.
   */
  const goToBridgeIntelligence = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'bridge');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  /*
   * The `showDependencyReview` / `showValueRealization` toggles are GONE (S5
   * production). Both are peer views now, so a second visibility flag would be a
   * second source of truth about whether the reader is looking at them — and the
   * Value Curve was rendered in the sidebar AND reachable as a toggle, so it could be
   * on screen twice.
   */

  // State: Cost Configuration
  const [showCostConfig, setShowCostConfig] = useState(false);
  const [showRegenerateConfig, setShowRegenerateConfig] = useState(false);
  /**
   * What the plan was priced on, as the server reports it (S4d).
   *
   * Replaces the `orgContext` state that both pre-filled the form AND supplied the
   * generate request's fallback context. Holding those numbers on the client was
   * the mechanism by which "Skip — Use Defaults" produced a fully-costed plan.
   */
  const [assumptionSummary, setAssumptionSummary] = useState<AssumptionSummaryEntry[] | null>(null);
  const [projectCosts, setProjectCosts] = useState<ProjectCosts | null>(null);
  const [costSummary, setCostSummary] = useState<CostSummary | null>(null);
  /*
   * STABLE identity (S4h). An inline `(summary) => setCostSummary(summary)`
   * re-created the callback every render; CostConfigurationPanel's notify
   * effect depended on it, so cost change → setCostSummary → re-render → new
   * callback → effect refires → setCostSummary … — the "Maximum update depth
   * exceeded" crash on Configure Costs. The panel side is now stable, and the
   * child holds the callback in a ref so its effect keys on costs alone.
   */
  const handleCostChange = useCallback((summary: CostSummary) => setCostSummary(summary), []);

  // ============================================================================
  // API: Load existing action plan
  // ============================================================================

  const loadActionPlan = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotice(null);

      const response = await authFetch(`/api/journeys/${journeyId}/action-plan`);

      if (response.status === 404) {
        // No action plan exists yet - show "Generate" button
        setActionPlan(null);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        /*
         * STANDARDIZED ERROR SHAPE (S4h). The server sends
         * `{ error: { code, message, details?, requestId } }` — and
         * `new Error(errorData.error)` stringified that OBJECT, so the error
         * screen read "[object Object]" instead of the server's message. The
         * message is rendered; a legacy string `error` still passes through;
         * and an unparseable body falls back to the HTTP status rather than
         * throwing inside the error path.
         */
        const body = await response.json().catch(() => null);
        const message =
          typeof body?.error === 'string' ? body.error : body?.error?.message;
        throw new Error(
          typeof message === 'string' && message.length > 0
            ? message
            : `Failed to load action plan (HTTP ${response.status})`,
        );
      }

      const data = await response.json();
      setActionPlan(data.action_plan);
    } catch (err) {
      console.error('Error loading action plan:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [journeyId, authFetch]);

  // ============================================================================
  // API: Load persisted project costs
  // ============================================================================

  const loadProjectCosts = useCallback(async () => {
    try {
      const response = await authFetch(`/api/journeys/${journeyId}/action-plan/costs`);
      if (!response.ok) return;
      const data = await response.json() as { costs: ProjectCosts | null };
      if (!data.costs) return;
      recalculateCosts(data.costs);
      setProjectCosts(data.costs);
      setCostSummary(extractCostSummary(data.costs));
    } catch (err) {
      console.error('Error loading project costs:', err);
    }
  }, [journeyId, authFetch]);

  // ============================================================================
  // API: Check if Bridge AI insights exist
  // ============================================================================

  /**
   * Ask whether Bridge AI recommendations exist, and distinguish "no" from "could not
   * ask".
   *
   * Each mapping below is a deliberate reading of the response, not a default:
   *
   *   404  the server answered about this journey and has no intelligence resource for
   *        it. That IS absence.
   *   503  the AI service is unavailable. Says nothing about the journey — this was the
   *        headline conflation, sitting in the same branch as the 404.
   *   200 + { error } and no summary: DC-1630, a generation that failed after the headers
   *        went out. A failed attempt is not evidence that nothing exists.
   *   200 with a payload and no recommendations: absence, from an answer.
   *   any other non-2xx, or a thrown request: unanswered.
   *
   * `check_only=true` IS the contract of this function (post-merge staging finding,
   * fix/action-plan-prerequisite-state). Without it, the route's bare GET is
   * generate-on-miss: a journey with no cached intelligence had its full LLM synthesis
   * run synchronously by this CHECK — ~110–124s measured on staging — so opening the
   * tab showed "Checking prerequisites" for minutes and, worse, the passive check
   * mutated server state by generating and persisting intelligence. With check_only
   * the route answers from cache/PostgreSQL or 404s, which maps exactly onto the
   * absent state below; generation stays where the user can see and choose it
   * (Bridge Intelligence).
   *
   * The optional `signal` makes a superseded check inert: when the effect that issued
   * it has cleaned up (journey change, unmount, StrictMode double-invoke), the stale
   * response must not overwrite the current journey's prerequisite state — and an
   * abort is not an answer, so it never writes `unavailable` either.
   */
  const checkBridgeAI = useCallback(async (signal?: AbortSignal) => {
    setBridgePrerequisite({ state: 'checking' });
    try {
      const response = await authFetch(
        `/api/bridge/${journeyId}/intelligence?check_only=true`,
        signal ? { signal } : {},
      );
      if (signal?.aborted) return;

      if (response.status === 404) {
        setBridgePrerequisite({ state: 'absent' });
        return;
      }

      if (response.status === 503) {
        setBridgePrerequisite({
          state: 'unavailable',
          reason: 'The Bridge AI service did not respond.',
        });
        return;
      }

      if (!response.ok) {
        setBridgePrerequisite({
          state: 'unavailable',
          reason: `The check returned an unexpected status (${response.status}).`,
        });
        return;
      }

      const data = await response.json();
      if (signal?.aborted) return;
      if (data?.error && !data.executive_summary) {
        setBridgePrerequisite({
          state: 'unavailable',
          reason: 'A previous Bridge AI generation did not finish.',
        });
        return;
      }

      const count = Array.isArray(data?.recommendations) ? data.recommendations.length : 0;
      setBridgePrerequisite(
        count > 0 ? { state: 'ready', recommendation_count: count } : { state: 'absent' },
      );
    } catch (err) {
      // A superseded check is inert: its abort is a cleanup artifact, not an
      // answer about the journey, so it must not write `unavailable` over the
      // successor's state.
      if (signal?.aborted) return;
      console.error('Error checking Bridge AI:', err);
      setBridgePrerequisite({
        state: 'unavailable',
        reason: 'The check could not be completed.',
      });
    }
  }, [journeyId, authFetch]);

  /**
   * THE one entry point for every prerequisite check — initial mount, journey
   * change, StrictMode re-invoke, and the visible "Try again" retry — owning a
   * single ref-held AbortController. Starting a new check always aborts and
   * supersedes the prior one, whichever path issued it; the effect-scoped
   * controller alone left the RETRY unguarded, so a retry followed by a journey
   * change could still overwrite the successor journey (PR #219 review).
   */
  const checkControllerRef = useRef<AbortController | null>(null);
  const runPrerequisiteCheck = useCallback(() => {
    checkControllerRef.current?.abort();
    const controller = new AbortController();
    checkControllerRef.current = controller;
    return checkBridgeAI(controller.signal);
  }, [checkBridgeAI]);

  // ============================================================================
  // LIFECYCLE: Load action plan on mount
  // ============================================================================

  useEffect(() => {
    loadActionPlan();
    // Cleanup (journey change, unmount, StrictMode double-invoke) aborts
    // whatever check is CURRENT at that moment — including one a retry click
    // started after this effect ran — so no stale response can overwrite the
    // successor journey's prerequisite state.
    void runPrerequisiteCheck();
    loadProjectCosts();
    return () => checkControllerRef.current?.abort();
  }, [journeyId, loadActionPlan, runPrerequisiteCheck, loadProjectCosts]);

  // DC-1639: check whether this dataset's domain has a Remediation Action Plan.
  // Supplementary + fire-and-forget — a failure never blocks the panel.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await authFetch(
          `/api/triangulation/journey/${journeyId}/domain-action-plan-summary`,
        );
        if (!res.ok) return;
        const data = (await res.json()) as DomainCrossLink;
        if (!cancelled) setDomainCrossLink(data);
      } catch {
        /* silent — the cross-link is supplementary */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [journeyId, authFetch]);

  // ============================================================================
  // API: Generate new action plan
  // ============================================================================

  /**
   * Generate or regenerate, submitting EXACTLY the acknowledgments given (S4d).
   *
   * The `|| { dataset_count: 60, hourly_rate: 150, team_size: 10, ... }` fallback
   * this replaces is the client half of P0-4: pressing "Skip — Use Defaults" sent
   * a full, invented organization context, so a plan the user had explicitly
   * declined to configure came back priced to the dollar. An empty array now
   * means an empty array.
   *
   * Generation and regeneration call this same function with the same contract, so
   * provenance cannot differ between the two paths.
   */
  const generateActionPlan = async (acknowledged: AcknowledgedAssumption[] = []) => {
    try {
      setGenerating(true);
      setError(null);
      setAssumptionSummary(null);

      /*
       * Explicitly typed, not an anonymous object literal (S4d follow-up).
       *
       * `post<TResponse>` does not constrain its body, so the previous literal
       * could have sent a misspelled or retired field and compiled cleanly —
       * which is exactly how `organization_context` would have slipped back in.
       */
      const body: GenerateActionPlanRequest = {
        options: {
          include_business_value: true,
          dependency_mode: 'ai_assisted',
        },
        // Omitted entirely when nothing was acknowledged. Sending an empty array
        // and sending a default set are very different requests.
        ...(acknowledged.length > 0 ? { acknowledged_assumptions: acknowledged } : {}),
      };

      const data = await post<GenerateActionPlanResponse>(
        `/api/journeys/${journeyId}/action-plan`,
        body,
      );
      setActionPlan(data.action_plan);
      // The same summary is shown after generation AND regeneration, from the
      // server's own account of what the plan was priced on.
      setAssumptionSummary(data.assumption_summary ?? null);

      // Warnings used to go to `console.warn` — present in the payload, invisible
      // in the product (P1-8). They are now rendered from `action_plan.warnings`,
      // which the server persists, so they survive a reload rather than existing
      // only in the generate response.
    } catch (err) {
      console.error('Error generating action plan:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setGenerating(false);
    }
  };

  // ============================================================================
  // API: Update task status
  // ============================================================================

  const updateTaskStatus = async (taskId: string, status: TaskStatus) => {
    if (!actionPlan) return;

    const current = actionPlan.tasks.find(t => t.id === taskId);
    if (!current) return;

    try {
      // The actor is NOT sent. The server resolves it from the verified JWT and
      // rejects a body that names one — a client could otherwise attribute a
      // completion to any user id it chose.
      //
      // `expected_lock_version` says "I am changing the state I actually saw".
      // Without it the server cannot distinguish a deliberate overwrite from a
      // lost update.
      // authFetch directly, NOT the shared `patch` helper: that helper collapses
      // every non-2xx into a plain Error carrying only the message, so
      // `err.status` is always undefined and a 409 branch downstream can never
      // run. Preserving the status here keeps the fix local — useApiClient has
      // 62 importers and changing its error shape is a separate, much wider
      // change than this remediation should make.
      const response = await authFetch(
        `/api/journeys/${journeyId}/action-plan/tasks/${taskId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status, expected_lock_version: current.lock_version ?? 1 }),
        }
      );

      if (response.status === 409) {
        // Someone else moved this task since it was loaded, or it has no
        // tracked state. Reload rather than retry: retrying with the stale
        // token is the lost update the token exists to prevent.
        const conflict = await response.json().catch(() => null);
        const code = conflict?.error?.details?.code;
        const message =
          code === 'PLAN_NOT_PROJECTED'
            ? 'This task has no tracked state yet. Regenerate the plan before changing its status.'
            : 'This task was changed by someone else — the latest state has been loaded. Review it and re-apply your change if it still applies.';

        // The message is set AFTER the reload, not before. loadActionPlan()
        // begins with setError(null), so setting it first meant the documented
        // conflict message was cleared before it could ever render — the user
        // saw the task silently snap back with no explanation.
        // Reload first, THEN post the notice: loadActionPlan() begins with
        // setError(null)/setNotice(null), so posting first would clear it before
        // it could render.
        if (code !== 'PLAN_NOT_PROJECTED') await loadActionPlan();
        setNotice(message);
        return;
      }

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error?.message ?? `Failed to update task (HTTP ${response.status})`);
      }

      const data = (await response.json()) as UpdateTaskStateResponse;

      /*
       * FUNCTIONAL UPDATE AGAINST THE LATEST RENDERED TASKS (concurrency
       * repair). The previous `setActionPlan({ ...actionPlan, ... })` closed
       * over THIS call's snapshot: with two updates in flight, whichever
       * response landed second rebuilt the plan from a snapshot that predated
       * the first — erasing the first task's change. And the summary took the
       * server's `data.progress` verbatim, so RESPONSES ARRIVING REVERSED
       * regressed the displayed counts to an older server state.
       *
       * Now: (1) the update composes onto whatever tasks are CURRENTLY
       * rendered, so concurrent responses merge instead of overwriting; and
       * (2) displayed progress is RECOMPUTED from the resulting rendered task
       * set — the same set the chips and views draw — so it cannot regress or
       * disagree with what is on screen, whatever order responses land in.
       */
      setActionPlan((current) => {
        if (!current) return current;
        const tasks = current.tasks.map(task =>
          task.id === taskId
            ? {
                // MERGE the state delta into the task we already hold. Replacing
                // the task with the DTO would discard title, description,
                // dependencies, and the value model — content the server does
                // not send back because it does not own it.
                ...task,
                status: data.task.status as TaskStatus,
                completed_at: data.task.completed_at ?? undefined,
                completed_by: data.task.completed_by ?? undefined,
                lock_version: data.task.lock_version,
                state_provenance: data.task.state_provenance,
              }
            : task
        );
        const counted = { not_started: 0, in_progress: 0, completed: 0 };
        for (const t of tasks) {
          if (t.status === 'completed') counted.completed += 1;
          else if (t.status === 'in_progress') counted.in_progress += 1;
          else if (t.status === 'not_started') counted.not_started += 1;
          // blocked / skipped / malformed stay OUT of the three settable
          // counts — the header's five-state chips derive them from the
          // rendered tasks themselves.
        }
        return {
          ...current,
          summary: {
            ...current.summary,
            // The response deliberately carries no financial or timeline
            // fields, so value, ROI and duration are left exactly as they were.
            tasks_not_started: counted.not_started,
            tasks_in_progress: counted.in_progress,
            tasks_completed: counted.completed,
            // Denominator = the RENDERED set. Null (unavailable), never 0%,
            // when there is nothing to measure over.
            overall_progress_pct:
              tasks.length > 0 ? (counted.completed / tasks.length) * 100 : null,
          },
          tasks,
        };
      });

      if (data.affected_tasks && data.affected_tasks.length > 0) {
        console.log(`${data.affected_tasks.length} tasks unblocked`);
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  // DC-1639: banner cross-linking to the domain's Remediation Action Plan (a
  // distinct artifact — the accepted prescriptive plays). Rendered on both the
  // generated-plan view and the empty state. Hidden when there's nothing to link.
  const domainCrossLinkBanner =
    domainCrossLink?.exists && domainCrossLink.domain_id ? (
      <Link
        to={`/triangulation/${domainCrossLink.domain_id}`}
        className="ap-domain-crosslink"
        data-testid="domain-crosslink"
      >
        <span className="ap-domain-crosslink-text">
          Domain <strong>{domainCrossLink.domain_name}</strong> has a Remediation Action Plan —{' '}
          {domainCrossLink.total} accepted {domainCrossLink.total === 1 ? 'play' : 'plays'}
        </span>
        <span className="ap-domain-crosslink-cta">View domain plan →</span>
      </Link>
    ) : null;

  // ============================================================================
  // RENDER: Loading state
  // ============================================================================

  if (loading) {
    return (
      <div className="action-plan-panel loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading action plan...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Error state
  // ============================================================================

  if (error) {
    return (
      <div className="action-plan-panel error">
        {/* An alert (S4h): the failure replaces the whole surface, and assistive
            tech should hear the server's message, not discover silence. */}
        <div className="error-message" role="alert">
          <h3>Error Loading Action Plan</h3>
          <p>{error}</p>
          <button
            className="btn-primary"
            onClick={loadActionPlan}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Empty state (no action plan yet)
  // ============================================================================

  if (!actionPlan) {
    // Check if Bridge AI is a prerequisite
    /*
     * STATE-AWARE ENTRY, WITH ONLY REAL BLOCKERS (S5 production, H1).
     *
     * What was here invented a prerequisite ladder: three steps, with "Complete Journey
     * Stations" rendered with a ✅ unconditionally — it checked nothing. A reader was
     * told a step had been completed on no evidence, which is the same defect class as a
     * fabricated value.
     *
     * The server enforces exactly TWO blockers on generation:
     *   1. the journey must resolve within the organisation
     *      (`resolveJourneyIdentity`, actionPlanGenerator.ts)
     *   2. Bridge AI recommendations must exist for it
     *      (`loadBridgeData`, actionPlanGenerator.ts:337)
     *
     * There is NO FAIR-assessment prerequisite and no station-completion prerequisite.
     * The P1-1 eligibility state machine is deferred, so this renders the one blocker it
     * can actually observe and claims nothing about the others.
     */
    /*
     * CHECKING. No verdict and no generate affordance — the prerequisite has not been
     * established yet, and offering the inputs here is what made the old `null` state
     * dishonest.
     */
    if (bridgePrerequisite.state === 'checking') {
      return (
        <div className="action-plan-panel empty">
          <div className="empty-state" aria-busy="true">
            <h2>Checking prerequisites</h2>
            <p>Looking for the Bridge AI recommendations an Action Plan is built from.</p>
            <span className="spinner" aria-hidden="true"></span>
          </div>
        </div>
      );
    }

    /*
     * UNANSWERED. Distinct from absence, and phrased as a failure to determine rather
     * than a finding about the journey. A retry is the action, because the reader's
     * journey may be perfectly fine.
     */
    if (bridgePrerequisite.state === 'unavailable') {
      return (
        <div className="action-plan-panel empty">
          <div className="empty-state prerequisite">
            <h2>We could not check the prerequisites</h2>
            <p>
              An Action Plan is built from Bridge AI recommendations, and this journey
              could not be checked for them. {bridgePrerequisite.reason} This does not
              tell us whether recommendations exist.
            </p>

            <div className="prerequisite-actions">
              {/* Through the unified entry point, never checkBridgeAI directly:
                  the retry must be abortable and must supersede any prior check. */}
              <button className="btn-primary" type="button" onClick={() => void runPrerequisiteCheck()}>
                Try again
              </button>
              <button
                className="btn-secondary"
                type="button"
                onClick={goToBridgeIntelligence}
              >
                Open Bridge Intelligence
              </button>
            </div>
          </div>
        </div>
      );
    }

    /*
     * ABSENT — the ONLY state permitted to say the journey has none, because it is the
     * only one where the server answered the question.
     */
    if (bridgePrerequisite.state === 'absent') {
      return (
        <div className="action-plan-panel empty">
          <div className="empty-state prerequisite">
            <h2>Bridge AI insights are needed first</h2>
            <p>
              An Action Plan is built from Bridge AI recommendations, and this journey
              does not have any yet. Generate insights in Bridge Intelligence, then
              return here.
            </p>

            <button
              className="btn-primary"
              type="button"
              onClick={goToBridgeIntelligence}
            >
              Open Bridge Intelligence
            </button>

            <div className="prerequisite-info">
              <p className="info-subtext">
                This is the only content blocker the server reports. Nothing else about
                the journey is being asserted here.
              </p>
            </div>
          </div>
        </div>
      );
    }

    /*
     * READY. Reached only when the server confirmed recommendations exist.
     *
     * THE COPY HERE MADE PROMISES THE PIPELINE DOES NOT KEEP. It said the inputs produce
     * "accurate cost and value calculations", promised "ROI projections" flatly, and
     * listed five guaranteed outputs including "Net value and payback period
     * calculations". None of that is assured: the inputs are acknowledged ASSUMPTIONS,
     * every Pistoia figure is capped at `model_estimate` by the confidence and
     * attribution factors that always apply, and a parameter with no usable input yields
     * `unavailable` rather than a number. Promising a payback period and then rendering
     * "unavailable" is the fabrication defect moved into the marketing copy.
     */
    return (
      <div className="action-plan-panel empty">
        {domainCrossLinkBanner}
        <div className="empty-state">
          <h2>Generate Action Plan</h2>
          <p>
            Bridge AI has {bridgePrerequisite.recommendation_count}{' '}
            {bridgePrerequisite.recommendation_count === 1
              ? 'recommendation'
              : 'recommendations'}{' '}
            for this journey. Supplying your organization context lets the plan forecast
            cost and value; where an input is missing, the figures that depend on it stay
            unavailable rather than being estimated for you.
          </p>

          {/* Business Configuration Form - has its own Generate button */}
          <div className="business-config-section">
            <BusinessConfigForm
              journeyId={journeyId}
              onConfigured={(acknowledged) => {
                generateActionPlan(acknowledged);
              }}
              onSkip={() => {
                // "Continue without these inputs" — submits NO acknowledgments.
                generateActionPlan([]);
              }}
              mode="inline"
            />
          </div>

          {/* Show spinner if generating */}
          {generating && (
            <div className="generating-indicator">
              <span className="spinner"></span>
              <p>Generating Action Plan...</p>
            </div>
          )}

          <div className="empty-features">
            <h4>What the plan contains</h4>
            <ul>
              <li>A phased roadmap (Foundation → Standardization → Automation)</li>
              <li>Task dependencies and the resulting critical path</li>
              <li>
                Value and cost forecasts, each labelled with the source of the figure —
                observed, an assumption you acknowledged, or a model estimate
              </li>
              <li>
                Net value and payback period, when the inputs support them. Both are
                shown as unavailable when they do not.
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: Action plan view
  // ============================================================================

  return (
    <div className="action-plan-panel">
      {notice && (
        <div className="ap-conflict-notice" role="status" aria-live="polite">
          <span className="ap-conflict-notice__text">{notice}</span>
          <button
            type="button"
            className="ap-conflict-notice__dismiss"
            aria-label="Dismiss notice"
            onClick={() => setNotice(null)}
          >
            ×
          </button>
        </div>
      )}

      {/*
        What the plan does NOT know (P1-8).

        These were `string[]` handed to `console.warn`, so a plan with no FAIR
        baseline or no priced tasks presented its gaps as zeros on cards and said
        nothing about why. Rendered from the persisted `warnings` array, so they
        survive a reload rather than living only in the generate response.
      */}
      {domainCrossLinkBanner}
      {/* Regenerate — a REAL dialog (S4h): labelled, described, aria-modal,
        * focus entry/trap/return, Escape. Closing it never regenerates. */}
      {showRegenerateConfig && (
        <ActionPlanModal
          label="Regenerate Action Plan"
          description="Review the value inputs the regenerated plan will be priced on. Closing this dialog regenerates nothing and changes nothing."
          className="regenerate-config-modal"
          onClose={() => setShowRegenerateConfig(false)}
        >
          <div className="modal-header">
            <h2>Regenerate Action Plan</h2>
            <button
              className="btn-close"
              aria-label="Close Regenerate Action Plan"
              onClick={() => setShowRegenerateConfig(false)}
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            <BusinessConfigForm
              journeyId={journeyId}
              onConfigured={(acknowledged) => {
                setShowRegenerateConfig(false);
                generateActionPlan(acknowledged);
              }}
              onSkip={() => {
                setShowRegenerateConfig(false);
                // Same contract as first generation: nothing acknowledged.
                generateActionPlan([]);
              }}
              mode="modal"
            />
          </div>
        </ActionPlanModal>
      )}

      {/* Configure Costs — the same dialog contract. Closing saves nothing;
        * only the panel's own Save control persists. */}
      {showCostConfig && (
        <ActionPlanModal
          label="Configure Costs"
          description="Adjust the project cost model. Closing this dialog saves nothing — only the Save button persists changes."
          className="cost-config-slideout"
          onClose={() => setShowCostConfig(false)}
        >
          <CostConfigurationPanel
            journeyId={journeyId}
            initialCosts={projectCosts || undefined}
            orgSize={sizeFromAssumptions(assumptionSummary)}
            industry={industryFromAssumptions(assumptionSummary)}
            headcount={50}
            onCostChange={handleCostChange}
            onSave={async (costs) => {
              setProjectCosts(costs);
              setShowCostConfig(false);
              try {
                await put(`/api/journeys/${journeyId}/action-plan/costs`, { costs });
              } catch (err) {
                console.error('Error saving project costs:', err);
                setError(err instanceof Error ? err.message : 'Failed to save costs');
              }
            }}
            onClose={() => setShowCostConfig(false)}
          />
        </ActionPlanModal>
      )}

      {/* The decision header (S4e, D2) — one row that answers "what should we do
        * first?", with the detailed reporting behind its Plan facts disclosure. */}
      <ActionPlanHeader
        summary={actionPlan.summary}
        metadata={actionPlan.metadata}
        derived={actionPlan.derived}
        tasks={actionPlan.tasks}
        phases={actionPlan.phases}
        costSummary={costSummary}
        onRegenerate={() => setShowRegenerateConfig(true)}
        onConfigureCosts={() => setShowCostConfig(true)}
        isRegenerating={generating}
      />

      {/*
        What the plan does NOT know (P1-8), rendered DIRECTLY under the header so a
        warning sits beside the figures it qualifies (S4e). Where a warning names a
        missing input, the action that resolves it rides with the message: the
        value-input codes open the regenerate flow, which is where inputs are
        acknowledged; the cost code opens Configure costs.
      */}
      {actionPlan.warnings && actionPlan.warnings.length > 0 && (
        <ul className="ap-plan-warnings" aria-label="Plan limitations">
          {actionPlan.warnings.map((warning) => {
            const resolvesViaInputs = [
              'VALUE_INPUTS_NOT_ACKNOWLEDGED',
              'VALUE_INPUTS_ESTIMATED',
              'NO_TASK_VALUED',
              'TASKS_PARTIALLY_VALUED',
            ].includes(warning.code);
            return (
              <li
                key={`${warning.code}-${warning.field ?? 'plan'}`}
                className={`ap-plan-warning ap-plan-warning--${warning.severity}`}
              >
                <span className="ap-plan-warning__severity">{warning.severity}</span>
                <span className="ap-plan-warning__message">{warning.message}</span>
                {resolvesViaInputs && (
                  <button
                    type="button"
                    className="ap-plan-warning__action"
                    onClick={() => setShowRegenerateConfig(true)}
                  >
                    Review value inputs
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/*
        * SIX PEER VIEWS, MATRIX FIRST (S5 production).
        *
        * Was four tabs plus two side toggles, so Dependency and Value Curve were not
        * views: they could not be linked to, could not hold selection, and reaching one
        * meant leaving whatever view you were in. They are peers now.
        *
        * `role="tablist"` with `aria-selected` rather than a `.active` class alone, so a
        * screen reader is told which view is current instead of inferring it from
        * styling. Rendered from ACTION_PLAN_VIEWS so the tab order and the vocabulary
        * cannot drift apart.
        */}
      <div className="view-controls">
        {viewMode !== 'matrix' && selectedTaskId &&
          actionPlan.tasks.some((t) => t.id === selectedTaskId) && (
          <button
            type="button"
            className="ap-show-on-matrix"
            onClick={() => showTaskOnMatrix(selectedTaskId)}
          >
            Show on Matrix →
          </button>
        )}
        <div
          className="view-mode-tabs ap-view-tabs"
          role="tablist"
          aria-label="Action Plan views"
        >
          {ACTION_PLAN_VIEWS.map((mode) => (
            <button
              key={mode}
              id={`ap-view-tab-${mode}`}
              type="button"
              role="tab"
              aria-selected={viewMode === mode}
              aria-controls="ap-view-panel"
              className={`view-tab ${viewMode === mode ? 'active' : ''}`}
              onClick={() => setViewMode(mode)}
            >
              {ACTION_PLAN_VIEW_LABELS[mode]}
            </button>
          ))}
        </div>
      </div>

      {/* Main content area */}
      <div className="action-plan-content">
        {/* Left column: Main view */}
        <div
          className="main-view"
          id="ap-view-panel"
          role="tabpanel"
          aria-labelledby={`ap-view-tab-${viewMode}`}
        >
          {viewMode === 'timeline' && (
            <TimelineView
              tasks={actionPlan.tasks}
              phases={actionPlan.phases}
              dependencies={actionPlan.dependencies}
              onTaskClick={setSelectedTaskId}
              selectedTaskId={selectedTaskId}
              filters={matrixFilters}
              onClearFilters={clearMatrixFilters}
              _onTaskStatusChange={updateTaskStatus}
            />
          )}

          {viewMode === 'list' && (
            <ListView
              tasks={actionPlan.tasks}
              phases={actionPlan.phases}
              dependencies={actionPlan.dependencies}
              onTaskClick={setSelectedTaskId}
              selectedTaskId={selectedTaskId}
              filters={matrixFilters}
              onClearFilters={clearMatrixFilters}
              onTaskStatusChange={updateTaskStatus}
            />
          )}

          {viewMode === 'matrix' && (
            <MatrixView
              tasks={actionPlan.tasks}
              phases={actionPlan.phases}
              onTaskClick={setSelectedTaskId}
              selectedTaskId={selectedTaskId}
              onTaskStatusChange={updateTaskStatus}
              filters={matrixFilters}
              onFiltersChange={setMatrixFilters}
              onClearFilters={clearMatrixFilters}
            />
          )}

          {viewMode === 'prism' && (
            <div className="prism-view-container">
              <IntelligencePrism
                journeyId={journeyId}
                onStationClick={(stationId, stationName) =>
                  drillToTask(
                    (t) =>
                      (stationName !== undefined && t.station_name === stationName) ||
                      normalizeStation(t.station_name) === normalizeStation(stationId),
                    `No plan task originates from ${stationName ?? stationId}.`,
                  )
                }
                onDriverClick={(driverId) =>
                  drillToTask(
                    (t) => (t.business_value_drivers ?? []).includes(driverId),
                    `No plan task is driven by “${driverId}”.`,
                  )
                }
              />
            </div>
          )}

          {/*
            * Dependency and Value Curve as PEER VIEWS rather than side toggles.
            * Selection is passed through so returning to Matrix lands on the same task.
            */}
          {viewMode === 'dependency' && (
            <DependencyReviewPanel
              tasks={actionPlan.tasks}
              dependencies={actionPlan.dependencies}
              selectedTaskId={selectedTaskId}
              onClose={() => setViewMode(DEFAULT_ACTION_PLAN_VIEW)}
            />
          )}

          {viewMode === 'valuecurve' && (
            <ValueRealizationChart
              schedule={actionPlan.value_realization}
              summary={actionPlan.summary}
              tasks={actionPlan.tasks}
              selectedTaskId={selectedTaskId}
              onTaskClick={setSelectedTaskId}
            />
          )}
        </div>

        {/*
          * Right sidebar: progress only.
          *
          * The Value Curve moved OUT of here — it is a peer view now, and rendering it in
          * both places meant the same chart could be on screen twice with no way to tell
          * which one the reader meant.
          */}
        <div className="sidebar">
          {/*
            * THE SHARED TASK DRAWER (2B part 2).
            *
            * Rendered by the panel, not by a view, so the same drawer serves every view
            * and selection stays canonical in the URL. It appears only for a selection
            * the rendered plan actually contains — the stale-selection guard above
            * clears anything else once the plan has loaded.
            */}
          {selectedTaskId && (() => {
            const selectedTask = actionPlan.tasks.find((t) => t.id === selectedTaskId);
            return selectedTask ? (
              <ActionPlanTaskDrawer
                task={selectedTask}
                tasks={actionPlan.tasks}
                phases={actionPlan.phases}
                onClose={() => setSelectedTaskId(null)}
              />
            ) : null;
          })()}
          <ProgressPanel
            summary={actionPlan.summary}
            phases={actionPlan.phases}
          />
        </div>
      </div>
    </div>
  );
};
